// Power-line conductors: thin metal wires, not black hairlines (owner
// 2026-11-01, APP_BARRIERS "Draw"). The cable lines of THE objects group
// (objects.js) stay ONE GL LineSegments draw — a GL line is always one pixel
// wide — and this material gives each conductor:
//   * COVERAGE: its real diameter projected per eye — px/m = P[1][1] x the
//     CURRENT viewport's height / 2 (onBeforeRender: each XR eye, the optic's
//     render target, the phone canvas), ÷ the clip w — is the pixel's alpha,
//     clamped to [0, 1]: the standard sub-pixel "wire AA". A far wire fades out
//     the way a real one does; nearer than ~d x px/m (≈ 20-30 m) it is a
//     one-pixel opaque line (thinner than its true 2-3 px there).
//   * LIGHT: the bake's own light model (meta.tex.light: E = gnd + (sky - gnd)
//     w + sun x Lambert, w = 0.5 n_y + 0.5; the sun = meta.lighting, the
//     water glint's uSunL), averaged over the cylinder's VISIBLE half: the
//     hemisphere term at mean n_y = π/4 · v_y, the sun term |L⊥| (sin φ +
//     (π - φ) cos φ) / 4 (a Lambert cylinder's phase, φ the angle between
//     the sun and the eye around the wire), x a weathered-aluminium albedo.
//     No scene light, the one display curve (`look`), the scene fog.
//   * GLINT: the specular cylinder's sun reflection lies on a cone about the
//     wire (T·R = -T·L); its radiance, per unit of projected width, is
//     F E_sun cos(φ/2) g(Δ) / (4 sin β) (x π: this app's E-scaled radiance),
//     g a normalised Gaussian lobe in the out-of-cone angle Δ = asin(T·V) +
//     asin(T·L), β the eye's angle to the wire. A few ALU per fragment of a
//     one-pixel line.
// The fence wires (BarrierTail's near tier: chain-link / electric / wire
// fences) share the buffer and the rule, each with its kind's diameter.
import * as THREE from 'three';
import { sunDirFromLighting } from './scenelight.js';

// wire diameters (m). Conductors, EN 50182 Al/St stranded: 550/70 (32.4 mm, one
// sub-conductor of a >= 220 kV bundle), 240/40 (21.9 mm, 50-150 kV and an
// untagged tower line / the TLM Stromleitung), 95/15 (13.6 mm, distribution:
// a pole line or a tagged < 50 kV line). Fences: barbed wire = two twisted
// 1.7 mm strands + barbs (4 mm), electric = polywire (3 mm); a chain-link
// fence's lines stand in for its mesh, so each carries the mesh's AREA
// coverage over the band it represents: 50 mm mesh of 2.8 mm wire covers
// 2 (2.8/50) - (2.8/50)^2 = 0.109 of the face, x the line pitch (h x 0.22)
export const WIRE_D = Object.freeze({ ehv: 0.0324, hv: 0.0219, dist: 0.0136, barbed: 0.004, poly: 0.003, mesh_cov: 0.109 });
export function wireDiameter(kv, kc) {
  if (kc === 1 || (kv > 0 && kv < 50)) return WIRE_D.dist;
  return kv >= 220 ? WIRE_D.ehv : WIRE_D.hv;
}
// fence style (objects.js FENCE_ST: 5 chain, 6 electric, 7 wire) + height -> m
export function fenceWireD(st, h) {
  return st === 5 ? WIRE_D.mesh_cov * h * 0.22 : st === 6 ? WIRE_D.poly : WIRE_D.barbed;
}
// the look: diffuse albedo of weathered aluminium / galvanised strands, the
// specular share (albedo + spec <= 1), the glint lobe's sigma in radians
// (stranding + weathering spread the mirror cone)
export const WIRE_LOOK = Object.freeze({ albedo: 0.5, spec: 0.35, lobe_rad: 0.1 });
export const WIRE_ORDER = 3;   // among transparents: after the water (1) and station domes (2), before beacons (5)

// px per metre at clip w = 1 for this projection + viewport height (px)
export const wirePxPerM = (proj, viewH) => proj[5] * 0.5 * viewH;
// the coverage (the line's alpha) of a wire of diameter d at clip w
export const wireAlpha = (d, pxPerM, w) => Math.min(1, Math.max(0, d * pxPerM / w));
export const WIRE_ALPHA_GLSL = 'clamp( vWire.w * uPxM / vClipW, 0.0, 1.0 )';

const VERT = `
#include <common>
#include <fog_pars_vertex>
attribute vec4 aWire;
varying vec3 vWp;
varying vec4 vWire;
varying float vClipW;
void main() {
  vec4 wp = modelMatrix * vec4( position, 1.0 );
  vWp = wp.xyz;
  vWire = vec4( mat3( modelMatrix ) * aWire.xyz, aWire.w );
  vec4 mvPosition = viewMatrix * wp;
  gl_Position = projectionMatrix * mvPosition;
  vClipW = gl_Position.w;
  #include <fog_vertex>
}`;

const FRAG = `
#include <common>
#include <fog_pars_fragment>
uniform float uPxM;
uniform vec3 uSky, uGnd, uSunC, uSunL, uLook;
varying vec3 vWp;
varying vec4 vWire;
varying float vClipW;
void main() {
  vec3 T = normalize( vWire.xyz ), V = normalize( cameraPosition - vWp ), L = uSunL;
  float tv = dot( T, V ), tl = dot( T, L );
  vec3 Vp = V - tv * T, Lp = L - tl * T;
  float vl = length( Vp ), ll = length( Lp );
  vec3 v = Vp / max( vl, 1e-4 ), l = Lp / max( ll, 1e-4 );
  float cp = clamp( dot( v, l ), -1.0, 1.0 ), up = step( 0.0, L.y );
  // the sky / ground hemisphere at the visible half's mean normal + the sun's Lambert-cylinder phase
  float lam = ll * ( sqrt( 1.0 - cp * cp ) + ( PI - acos( cp ) ) * cp ) * 0.25;
  vec3 E = mix( uGnd, uSky, 0.5 + 0.5 * 0.78539816 * v.y ) + uSunC * ( lam * up );
  // the glint: the sun's reflection cone about the wire (asin(T.V) = -asin(T.L) on it)
  float dl = asin( clamp( tv, -1.0, 1.0 ) ) + asin( clamp( tl, -1.0, 1.0 ) );
  float g = exp( -0.5 * dl * dl / ( uLook.z * uLook.z ) ) / ( uLook.z * 2.50662827 );
  vec3 S = uSunC * ( PI * 0.25 * uLook.y * sqrt( 0.5 + 0.5 * cp ) * g / max( vl, 0.2 ) * up );
  gl_FragColor = vec4( uLook.x * E + S, ${WIRE_ALPHA_GLSL} );
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
  #include <fog_fragment>
}`;

// the wire material for this world: the light read unconditionally (a bake
// without meta.tex.light / meta.lighting throws, like the ground's)
export function wireMaterial(meta) {
  const L = meta && meta.tex && meta.tex.light;
  if (!(L && Array.isArray(L.sky) && Array.isArray(L.ground) && Array.isArray(L.sun))) {
    throw new Error('world meta.tex.light missing (sky / ground / sun): rebuild this world (Build)');
  }
  const d = sunDirFromLighting(meta.lighting);
  if (!d) throw new Error('world meta.lighting missing (sun_az_deg / sun_alt_deg): rebuild this world (Build)');
  const m = new THREE.ShaderMaterial({
    name: 'wire', vertexShader: VERT, fragmentShader: FRAG, fog: true,
    transparent: true, depthWrite: false,
    uniforms: THREE.UniformsUtils.merge([THREE.UniformsLib.fog, {
      uPxM: { value: 0 },
      uSky: { value: new THREE.Vector3().fromArray(L.sky) },
      uGnd: { value: new THREE.Vector3().fromArray(L.ground) },
      uSunC: { value: new THREE.Vector3().fromArray(L.sun) },
      uSunL: { value: new THREE.Vector3().fromArray(d) },
      uLook: { value: new THREE.Vector3(WIRE_LOOK.albedo, WIRE_LOOK.spec, WIRE_LOOK.lobe_rad) },
    }]),
  });
  return m;
}

// the per-eye pixel scale, set where three draws the line: onBeforeRender
// runs per camera of an ArrayCamera (XR) after the eye's viewport is set
const _vp = new THREE.Vector4();
export function wireBeforeRender(renderer, _scene, camera, _geometry, material) {
  renderer.getCurrentViewport(_vp);
  material.uniforms.uPxM.value = wirePxPerM(camera.projectionMatrix.elements, _vp.w);
  material.uniformsNeedUpdate = true;
}

// THIN RIBBONS (vr-shimmer, 2026-11-02; APP_SURFACE_PERF "vr-shimmer"): a strip of real geometry narrower
// than a pixel — the aerialway's cross-ribbon ropes (0.07-0.13 m) past ~100 m, a stream ribbon (2-10 m wide)
// seen at a grazing angle — covers a pixel's samples only now and then and flickers on / off under the head's
// sub-pixel motion (owner, Quest 3: "the cables of a cable car flicker constantly", "rivers … go off and on").
// The standard sub-pixel treatment: every vertex pair (2k, 2k+1) straddles its axis point (aOff = the vertex
// minus the pair's midpoint); the vertex shader widens a pair to at least RIBBON_MIN_PX on screen and the
// fragment's alpha is the true width over the drawn one — the same covered area, now a stable faint line.
// Where the strip is wider than that the geometry is untouched (scale 1). The width on screen:
//   'round' (a rope: the same from every side) — 2|aOff| x px/m (P[1][1] x the CURRENT viewport's height / 2,
//           as the wires above) / clip w;
//   'flat'  (a ribbon lying on the ground: its axis horizontal, at right angles to aOff) — the screen distance
//           between the pair's axis point and the vertex ACROSS the projected axis (the component perpendicular to
//           the axis' screen tangent, the tangent = up x aOff), x 2. Widened in SCREEN space (vr-thin-rest,
//           2026-11-02): the vertex moves along that perpendicular in NDC, its own clip z / w kept — exactly
//           RIBBON_MIN_PX across at any scale, at the true vertex's depth. (w24 scaled the WORLD offset by the whole
//           projected offset's length, capped x16: off the view's centre perspective turns a cross-stream offset
//           mostly ALONG the line on screen, so the side of the view saw ~3 px and was not widened while the true
//           thickness was 0.04 px; at a grazing view the world offset also runs along the view, x16 up / down the
//           banks into the ground.) Capped at RIBBON_MAX_S: beyond it the line's alpha is <= 1/64 (under one
//           8-bit step), a steady fade. Unwidened (scale 1) the vertex is three's own gl_Position.
export const RIBBON_MIN_PX = 1.5;
export const RIBBON_MAX_S = 64;
export const RIBBON_ORDER = WIRE_ORDER;   // transparent like the wires: after the water, with the wires
// aOff for a geometry built of vertex pairs (each pair's two vertices about its axis point)
export function ribbonOffsets(pos) {
  const off = new Float32Array(pos.length);
  for (let v = 0; v + 5 < pos.length; v += 6) {
    for (let c = 0; c < 3; c++) {
      const h = (pos[v + c] - pos[v + 3 + c]) / 2;
      off[v + c] = h; off[v + 3 + c] = -h;
    }
  }
  return off;
}
const _f2 = (x) => x.toFixed(2);
// the vertex block (replaces three's begin_vertex): `transformed` widened, vRbCov the coverage
export function ribbonVert(mode) {
  if (mode === 'flat') return `
  vec3 transformed = vec3( position );
  vec2 rbN = vec2( 0.0 );   // the extra screen offset (px) of a widened vertex
  float rbS = 1.0;
  {
    vec3 rbC = position - aOff;
    vec3 rbT = vec3( aOff.z, 0.0, -aOff.x );   // the axis' tangent: up x aOff
    vec4 rbA = projectionMatrix * modelViewMatrix * vec4( rbC, 1.0 );
    vec4 rbB = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
    vec4 rbE = projectionMatrix * modelViewMatrix * vec4( rbC + rbT / max( length( rbT ), 1e-6 ), 1.0 );
    if ( dot( rbT, rbT ) > 0.0 && rbA.w > 1e-3 && rbB.w > 1e-3 && rbE.w > 1e-3 ) {
      vec2 rbH = 0.5 * uRbView, rbA2 = rbA.xy / rbA.w * rbH;
      vec2 rbD = rbB.xy / rbB.w * rbH - rbA2, rbG = rbE.xy / rbE.w * rbH - rbA2;
      float rbGl = length( rbG );
      vec2 rbP = rbGl > 1e-6 ? vec2( -rbG.y, rbG.x ) / rbGl : rbD / max( length( rbD ), 1e-6 );
      float rbX = dot( rbD, rbP );   // the half width across the line, px (signed)
      rbS = clamp( ${_f2(RIBBON_MIN_PX)} / max( 2.0 * abs( rbX ), 1e-4 ), 1.0, ${_f2(RIBBON_MAX_S)} );
      rbN = rbP * ( rbX * ( rbS - 1.0 ) );
    }
    vRbCov = 1.0 / rbS;   // a geometry without aOff draws as it is (scale 1)
  }`;
  return `
  vec3 transformed = vec3( position );
  {
    vec3 rbC = position - aOff;
    vec4 rbA = projectionMatrix * modelViewMatrix * vec4( rbC, 1.0 );
    float rbPx = length( aOff ) * projectionMatrix[ 1 ][ 1 ] * uRbView.y / max( rbA.w, 1e-4 );
    float rbS = max( 1.0, ${_f2(RIBBON_MIN_PX)} / max( rbPx, 1e-4 ) );
    rbS = dot( aOff, aOff ) > 0.0 ? rbS : 1.0;   // a geometry without aOff draws as it is
    transformed = rbC + aOff * rbS;
    vRbCov = 1.0 / rbS;
  }`;
}
// 'flat': after three's project_vertex, a widened vertex moved across its line by rbN px (screen space)
export const RIBBON_FLAT_PROJECT = `#include <project_vertex>
  if ( rbS > 1.0 ) gl_Position.xy += rbN / ( 0.5 * uRbView ) * gl_Position.w;`;
export const RIBBON_DECL_V = '\nattribute vec3 aOff;\nuniform vec2 uRbView;\nvarying float vRbCov;';
export const RIBBON_DECL_F = '\nvarying float vRbCov;';
// a shader (onBeforeCompile's) made a thin-ribbon one; the fragment's coverage is the caller's (vRbCov)
export function ribbonShader(sh, mat, mode) {
  sh.uniforms.uRbView = mat.userData.rbView;
  sh.vertexShader = sh.vertexShader
    .replace('#include <common>', '#include <common>' + RIBBON_DECL_V)
    .replace('#include <begin_vertex>', ribbonVert(mode));
  if (mode === 'flat') sh.vertexShader = sh.vertexShader.replace('#include <project_vertex>', RIBBON_FLAT_PROJECT);
  sh.fragmentShader = sh.fragmentShader.replace('#include <common>', '#include <common>' + RIBBON_DECL_F);
}
// the per-pass viewport (each XR eye / the multiview pair, the optic's target, the canvas): the material's
// own onBeforeRender (three r170 calls it per draw, after the pass's viewport is set)
export function ribbonUniforms(mat) {
  const view = mat.userData.rbView = { value: new THREE.Vector2(1, 1) };
  mat.onBeforeRender = (renderer) => {
    renderer.getCurrentViewport(_vp);
    if (view.value.x !== _vp.z || view.value.y !== _vp.w) { view.value.set(_vp.z, _vp.w); mat.uniformsNeedUpdate = true; }
  };
}
// a MeshBasicMaterial (any built-in with the begin_vertex / alphatest chunks) made a 'round' thin-ribbon one
export function thinRibbonMaterial(mat) {
  mat.transparent = true;
  ribbonUniforms(mat);
  mat.onBeforeCompile = (sh) => {
    ribbonShader(sh, mat, 'round');
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <alphatest_fragment>', 'diffuseColor.a *= vRbCov;\n#include <alphatest_fragment>');
  };
  mat.customProgramCacheKey = () => 'thin-ribbon';
  return mat;
}
